<template>
	<v-expansion-panels :model-value="expanded ? panelKey : undefined" @update:model-value="$emit('update:expanded', $event === panelKey)">
		<v-expansion-panel :value="panelKey" class="widget-builder--panel" eager>
			<v-expansion-panel-title>
				<v-icon>{{ icon }}</v-icon>
				{{ title }}
				<v-badge color="primary" :content="chartCount" inline></v-badge>
			</v-expansion-panel-title>
			<v-expansion-panel-text>
				<div class="metric-analysis" :class="{ 'metric-analysis--stacked': stacked }">
					<ChartCard v-if="showEvolution" class="metric-analysis__chart" :title="t('MonthlyEvolution')" icon="bar_chart" :options="evolutionOptions" :is-empty="isEmpty" :scrollable="!stacked" :file-name="`${panelKey}-evolution`">
						<template v-if="stacked" #subheader>
							<v-chip v-if="selectedBar" size="small" variant="tonal" closable @click:close="selectedBar = null">
								{{ selectedBar.month }} · {{ selectedBar.label }}
								<strong class="ml-1">{{ selectedBar.formatted }}</strong>
							</v-chip>
						</template>
					</ChartCard>

					<ChartCard v-if="isDonut" class="metric-analysis__chart" :title="t('CategoryDistribution')" icon="donut_small" :options="distributionOptions" :is-empty="isEmpty" :file-name="`${panelKey}-distribution`">
						<template #subheader>
							<SelectControl :model-value="level" :items="levelOptions" title-key="label" item-value="value" :label="t('CategoryLevel')" :sort-items="false" :clearable="false" density="compact" class="metric-analysis__level" @update:model-value="$emit('update:level', $event)" />
						</template>
						<template #aside>
							<CategoryBreakdownList :rows="categoryRows" variant="legend" />
						</template>
					</ChartCard>

					<v-card v-else class="metric-analysis__chart" flat>
						<v-card-title class="metric-analysis__list-title">
							<v-icon>bar_chart</v-icon>
							{{ t('CategoryDistribution') }}
							<v-card-subtitle class="metric-analysis__list-subheader">
								<SelectControl :model-value="level" :items="levelOptions" title-key="label" item-value="value" :label="t('CategoryLevel')" :sort-items="false" :clearable="false" density="compact" class="metric-analysis__level" @update:model-value="$emit('update:level', $event)" />
							</v-card-subtitle>
						</v-card-title>

						<v-card-text>
							<CategoryBreakdownList :rows="categoryRows" variant="bars" />
						</v-card-text>
					</v-card>
				</div>
			</v-expansion-panel-text>
		</v-expansion-panel>
	</v-expansion-panels>
</template>

<script setup>
import { ref, computed, watch, defineAsyncComponent } from 'vue';
import { useTranslator } from '@/composables/useTranslator';
const ChartCard = defineAsyncComponent(() => import('@/components/common/ChartCard.vue'));
import { buildEvolutionOptions, buildDistributionOptions, buildCategoryRows, isDistributionDonut } from '@/components/orderTracking/orderTrackingCharts';
import CategoryBreakdownList from '@/components/orderTracking/CategoryBreakdownList.vue';
import SelectControl from '@/components/controls/SelectControl.vue';
import { METRIC } from '@/services/directOrderService';

const props = defineProps({
	title: { type: String, default: '' },
	icon: { type: String, default: 'insights' },
	panelKey: { type: String, default: 'metric' },
	metric: { type: String, default: METRIC.VALUE },
	monthlySeries: { type: Array, default: () => [] },
	categories: { type: Array, default: () => [] },
	level: { type: Number, default: 1 },
	levelOptions: { type: Array, default: () => [] },
	showReturns: { type: Boolean, default: false },
	stacked: { type: Boolean, default: false },
	expanded: { type: Boolean, default: true },
});

defineEmits(['update:expanded', 'update:level']);

const t = useTranslator();
const showEvolution = computed(() => props.monthlySeries.length > 1);
const chartCount = computed(() => (showEvolution.value ? 2 : 1));
const isEmpty = computed(() => props.categories.length === 0 && props.monthlySeries.every((month) => month.orderCount === 0));
const selectedBar = ref(null);
watch(
	() => props.monthlySeries,
	() => {
		selectedBar.value = null;
	}
);
const evolutionOptions = computed(() =>
	buildEvolutionOptions(props.monthlySeries, {
		metric: props.metric,
		showReturns: props.showReturns,
		labels: { ordered: t('Ordered'), delivered: t('Delivered'), returned: t('Returns') },
		hideYAxis: props.stacked,
		onBarClick: props.stacked ? (bar) => (selectedBar.value = bar) : null,
	})
);
const categoryRows = computed(() => buildCategoryRows(props.categories, { metric: props.metric, unclassifiedLabel: t('Unclassified') }));
const isDonut = computed(() => isDistributionDonut(props.categories));
const distributionOptions = computed(() => buildDistributionOptions(props.categories, { metric: props.metric, unclassifiedLabel: t('Unclassified'), totalLabel: t('Total') }));
</script>

<style scoped lang="scss">
.metric-analysis {
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 16px;
	min-width: 0;

	&--stacked {
		grid-template-columns: 1fr;
	}

	&__chart {
		min-width: 0;
	}

	&__level {
		max-width: 180px;
	}

	&__list-title {
		display: flex;
		align-items: center;
		gap: 8px;
		font-weight: 600;
	}

	&__list-subheader {
		padding-top: 8px;
	}
}
</style>
